import { useState } from 'react';
import { usePreviewPaymentMutation } from '@/entities/payment';
import type { PaymentPreviewAction, PaymentPreviewDiff, PreviewPaymentRequest } from '@/entities/payment';

interface PendingDialog {
  diff: PaymentPreviewDiff;
  resolve: (confirmed: boolean) => void;
}

/** §8.5: диалог предпросмотра последствий правки/разнесения/удаления платежа — тот же приём, что
 * и `useSaveWithPreview` для документов, но без `stateVersion`-повтора: у платежа нет отдельного
 * «состояния» многоактёрной конкуренции, единственный источник устаревания — `rowVersion` самого
 * платежа, который и предпросмотр, и реальное сохранение проверяют одинаково (устаревший
 * `rowVersion` просто всплывает как обычная ошибка VERSION_CONFLICT вызывающему коду). */
export function usePaymentSaveWithPreview() {
  const [previewPayment] = usePreviewPaymentMutation();
  const [pending, setPending] = useState<PendingDialog | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  function confirmDialog(diff: PaymentPreviewDiff): Promise<boolean> {
    return new Promise((resolve) => setPending({ diff, resolve }));
  }

  function resolvePending(confirmed: boolean) {
    pending?.resolve(confirmed);
    setPending(null);
  }

  async function runWithPreview(
    paymentId: string,
    action: PaymentPreviewAction,
    rowVersion: number,
    execute: () => Promise<unknown>,
    fields?: Omit<PreviewPaymentRequest, 'action' | 'rowVersion'>,
  ): Promise<boolean> {
    if (isRunning) return false;
    setIsRunning(true);
    try {
      const diff = await previewPayment({ id: paymentId, body: { action, rowVersion, ...fields } }).unwrap();
      if (diff.hasChanges) {
        const confirmed = await confirmDialog(diff);
        if (!confirmed) return false;
      }
      await execute();
      return true;
    } finally {
      setIsRunning(false);
    }
  }

  return { runWithPreview, pendingDiff: pending?.diff, resolvePending, isRunningPreview: isRunning };
}
